import { useState } from "react"
import { 
  BarChart3, 
  Download, 
  Calendar,
  TrendingUp,
  Users,
  DollarSign,
  ChevronDown,
  ChevronUp,
  FileText
} from "lucide-react"

// Mock data
const mockReports = [
  {
    id: 1,
    title: "Monthly Revenue Report",
    type: "Financial",
    date: "2024-03-01",
    status: "Generated",
    insights: [
      "Revenue increased by 15% compared to last month",
      "New student enrollments contributed to 30% of revenue",
      "Premium courses showed highest growth"
    ]
  },
  {
    id: 2,
    title: "Student Performance Analysis",
    type: "Academic",
    date: "2024-03-05",
    status: "Generated",
    insights: [
      "Average grade improved by 8%",
      "Mathematics showed highest improvement",
      "25% reduction in dropout rate"
    ]
  },
  {
    id: 3,
    title: "Teacher Evaluation Summary",
    type: "Performance",
    date: "2024-03-10",
    status: "Pending",
    insights: [
      "Overall satisfaction rate at 92%",
      "Interactive teaching methods highly rated",
      "Areas for improvement identified"
    ]
  }
]

export default function Reports() {
  const [timeRange, setTimeRange] = useState("7d")
  const [reports, setReports] = useState(mockReports)
  const [selectedType, setSelectedType] = useState("All Types")
  const [expandedReport, setExpandedReport] = useState(null)

  // Stats data
  const stats = [
    {
      title: "Total Reports",
      value: reports.length,
      change: "+12.5%",
      icon: FileText,
      color: "text-blue-600",
      trend: "up"
    },
    {
      title: "Generated Reports",
      value: reports.filter(r => r.status === "Generated").length,
      change: "+8.1%",
      icon: BarChart3,
      color: "text-green-600",
      trend: "up"
    },
    {
      title: "Pending Reports",
      value: reports.filter(r => r.status === "Pending").length,
      change: "-2.4%",
      icon: Calendar,
      color: "text-yellow-600",
      trend: "down"
    },
    {
      title: "Insights Generated",
      value: reports.reduce((acc, r) => acc + r.insights.length, 0),
      change: "+15.2%",
      icon: TrendingUp,
      color: "text-purple-600",
      trend: "up"
    }
  ]

  const handleGenerateReport = (type) => {
    const newReport = {
      id: Date.now(),
      title: `New ${type} Report`,
      type,
      date: new Date().toISOString().split('T')[0],
      status: "Generated",
      insights: [
        "New insight 1",
        "New insight 2",
        "New insight 3"
      ]
    }
    setReports([newReport, ...reports])
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-6 w-6" />
          <h2 className="text-2xl font-bold">Reports & Analytics</h2>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border rounded-lg bg-white"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <button className="px-4 py-2 bg-white text-gray-700 border rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export All
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.title} className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold mt-1">{stat.value}</p>
                <p className={`text-xs ${stat.trend === 'up' ? 'text-green-500' : 'text-red-500'} mt-1`}>
                  {stat.change} from last month
                </p>
              </div>
              <stat.icon className={`h-8 w-8 ${stat.color}`} />
            </div>
          </div>
        ))}
      </div>

      {/* Report Generation Buttons */}
      <div className="flex flex-wrap gap-4">
        <button
          onClick={() => handleGenerateReport("Financial")}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <DollarSign className="h-4 w-4" />
          Generate Financial Report
        </button>
        <button
          onClick={() => handleGenerateReport("Academic")}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
        >
          <Users className="h-4 w-4" />
          Generate Academic Report
        </button>
        <button
          onClick={() => handleGenerateReport("Performance")}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
        >
          <TrendingUp className="h-4 w-4" />
          Generate Performance Report
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="px-4 py-2 border rounded-lg bg-white"
        >
          <option>All Types</option>
          <option>Financial</option>
          <option>Academic</option>
          <option>Performance</option>
        </select>
      </div>

      {/* Reports List */}
      <div className="space-y-4">
        {reports
          .filter(report => selectedType === "All Types" || report.type === selectedType)
          .map((report) => (
            <div key={report.id} className="bg-white rounded-lg shadow-sm">
              <div 
                className="p-4 cursor-pointer"
                onClick={() => setExpandedReport(expandedReport === report.id ? null : report.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex flex-col">
                      <h3 className="text-lg font-semibold">{report.title}</h3>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{report.date}</span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${report.type === 'Financial' ? 'bg-blue-100 text-blue-800' :
                            report.type === 'Academic' ? 'bg-green-100 text-green-800' :
                            'bg-purple-100 text-purple-800'}`}>
                          {report.type}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${report.status === 'Generated' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {report.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <button className="p-2 hover:bg-gray-100 rounded-full">
                      <Download className="h-4 w-4 text-gray-500" />
                    </button>
                    {expandedReport === report.id ? (
                      <ChevronUp className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    )}
                  </div>
                </div>
              </div>
              
              {expandedReport === report.id && (
                <div className="px-4 pb-4 border-t">
                  <h4 className="text-sm font-medium text-gray-700 mt-4 mb-2">Key Insights:</h4>
                  <ul className="space-y-2">
                    {report.insights.map((insight, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                        {insight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  )
}